import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { amount as amt, money } from '../../lib/money';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  DataTable,
  EmptyState,
  FilterBar,
  KpiTile,
  PageContainer,
  PageHeader,
  Pagination,
  Spinner,
} from '../../ui/components';
import { Icon } from '../../ui/icons';
import { invoicePath } from '../../lib/slug';
import { buildFilterParams } from '../../lib/filters';
import { useT } from '../../i18n';
import {
  invoicesApi,
  invoiceStatusMeta,
  matchStatusMeta,
  INVOICE_STATUSES,
  MATCH_STATUSES,
} from './invoicesApi';
import '../requisitions/requisitions.css';
import './invoices.css';

const PAGE_SIZE = 25;
export default function InvoicesPage() {
  const t = useT();
  const navigate = useNavigate();

  const [data, setData] = useState({ items: [], totalCount: 0, page: 1, pageSize: PAGE_SIZE });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [values, setValues] = useState({});
  const [page, setPage] = useState(1);
  const [ap, setAp] = useState(null);
  const debounceRef = useRef(null);

  const fields = useMemo(() => [
    { id: 'statuses', label: t('Status'), type: 'multiselect', options: INVOICE_STATUSES.map((s) => ({ value: s.value, label: t(s.label) })) },
    { id: 'matchStatuses', label: t('Match'), type: 'multiselect', options: MATCH_STATUSES.map((s) => ({ value: s.value, label: t(s.label) })) },
    { id: 'hasVariance', label: t('Variance'), type: 'select', options: [{ value: 'true', label: t('Has variance') }, { value: 'false', label: t('Clean match') }] },
    { id: 'invoiced', label: t('Invoice date'), type: 'daterange', fromParam: 'invoicedFrom', toParam: 'invoicedTo' },
  ], [t]);

  const filterParams = useMemo(() => buildFilterParams(fields, values), [fields, values]);
  const hasFilters = search !== '' || Object.keys(filterParams).length > 0;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await invoicesApi.list({ page, pageSize: PAGE_SIZE, search, ...filterParams }));
    } catch (err) {
      setError(err?.message || t('Failed to load invoices.'));
    } finally {
      setLoading(false);
    }
  }, [page, search, filterParams, t]);

  useEffect(() => { load(); }, [load]);

  const loadAp = useCallback(async () => {
    try {
      setAp(await invoicesApi.apSummary());
    } catch { /* non-fatal: the panel just won't render */ }
  }, []);
  useEffect(() => { loadAp(); }, [loadAp, data.totalCount]);

  const onSearchChange = (value) => {
    setSearchText(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setPage(1); setSearch(value); }, 300);
  };

  const onFilterChange = (id, value) => {
    setPage(1);
    setValues((prev) => {
      const next = { ...prev };
      if (value === undefined) delete next[id];
      else next[id] = value;
      return next;
    });
  };

  const open = (row) => navigate(invoicePath(row));

  const columns = [
    { key: 'number', header: t('Invoice'), width: 130, render: (i) => <span style={{ fontFamily: 'var(--font-mono)' }}>{i.number}</span> },
    {
      key: 'supplier', header: t('Supplier'), width: 200,
      render: (i) => (
        <div>
          <div>{i.supplierName || '—'}</div>
          {i.supplierInvoiceNumber && <div className="pp-cell-muted" style={{ fontSize: 'var(--text-xs)' }}>{t('Ref {number}', { number: i.supplierInvoiceNumber })}</div>}
        </div>
      ),
    },
    { key: 'po', header: t('PO'), width: 120, render: (i) => <span style={{ fontFamily: 'var(--font-mono)' }}>{i.purchaseOrderNumber || '—'}</span> },
    {
      key: 'status', header: t('Status'), width: 110,
      render: (i) => { const m = invoiceStatusMeta(i.status); return <Badge tone={m.tone} dot>{t(m.label)}</Badge>; },
    },
    {
      key: 'match', header: t('Match'), width: 150,
      render: (i) => { const m = matchStatusMeta(i.matchStatus); return <Badge tone={m.tone}>{t(m.label)}</Badge>; },
    },
    { key: 'date', header: t('Invoice date'), width: 120, render: (i) => (i.invoiceDate ? new Date(i.invoiceDate).toLocaleDateString() : '—') },
    { key: 'total', header: t('Total'), width: 140, align: 'right', render: (i) => money(i.total) },
  ];

  const totalPages = Math.max(1, Math.ceil(data.totalCount / PAGE_SIZE));

  return (
    <PageContainer size="full">
      <PageHeader
        title={t('Invoices')}
        description={t('Capture supplier invoices against purchase orders. The system runs a three-way match (PO ↔ receipts ↔ invoice) and flags price and quantity variances before you approve and pay.')}
        icon={<Icon.Receipt size={22} />}
        actions={(
          <Button variant="primary" leadingIcon={<Icon.Plus size={16} />} onClick={() => navigate('/invoices/new')}>
            {t('New invoice')}
          </Button>
        )}
      />

      {ap && (
        <div className="ap-panel">
          <div className="req-kpis" style={{ marginBottom: 0 }}>
            <KpiTile
              label={t('Outstanding AP')}
              value={amt(ap.currency, ap.outstandingTotal)}
              meta={t('{count} approved, awaiting payment', { count: ap.outstandingCount })}
            />
            <KpiTile
              label={t('Overdue')}
              value={amt(ap.currency, ap.overdueTotal)}
              meta={ap.overdueCount > 0 ? t('{count} past due', { count: ap.overdueCount }) : t('Nothing past due')}
            />
            <KpiTile
              label={t('GRNI accrual')}
              value={amt(ap.currency, ap.grniTotal)}
              meta={`${t('Received, not invoiced')} · ${ap.grniPurchaseOrderCount} ${ap.grniPurchaseOrderCount === 1 ? t('PO') : t('POs')}`}
            />
            <KpiTile
              label={t('On hold')}
              value={amt(ap.currency, ap.disputedTotal)}
              meta={[
                t('{count} disputed', { count: ap.disputedCount }),
                ap.pendingApprovalCount ? t('{count} in approval', { count: ap.pendingApprovalCount }) : null,
                ap.draftCount ? t('{count} draft', { count: ap.draftCount }) : null,
              ].filter(Boolean).join(' · ')}
            />
          </div>

          {ap.outstandingTotal > 0 && (
            <Card style={{ marginTop: 'var(--space-3)' }}>
              <Card.Header>
                <Card.Title>{t('Payables aging')}</Card.Title>
                <div className="pp-cell-muted" style={{ fontSize: 'var(--text-sm)', marginTop: 2 }}>
                  {t('Approved invoices awaiting payment, aged by due date.')}
                </div>
              </Card.Header>
              <Card.Body>
                <div className="ap-aging">
                  {ap.aging.map((b) => {
                    const pct = ap.outstandingTotal > 0 ? Math.round((b.amount / ap.outstandingTotal) * 100) : 0;
                    const overdue = b.key !== 'current' && b.amount > 0;
                    return (
                      <div className="ap-aging__row" key={b.key}>
                        <span className="ap-aging__label">{t(b.label)}</span>
                        <span className="ap-aging__bar">
                          <span className={`ap-aging__fill ${overdue ? 'is-overdue' : ''}`} style={{ width: `${pct}%` }} />
                        </span>
                        <span className="ap-aging__amount">{amt(ap.currency, b.amount)}</span>
                        <span className="ap-aging__count">{b.count}</span>
                      </div>
                    );
                  })}
                </div>
              </Card.Body>
            </Card>
          )}
        </div>
      )}

      <FilterBar
        searchPlaceholder={t('Search by invoice, supplier or PO number…')}
        searchValue={searchText}
        onSearchChange={onSearchChange}
        fields={fields}
        values={values}
        onChange={onFilterChange}
      />

      {error ? (
        <EmptyState
          icon={<Icon.CircleAlert size={28} />}
          title={t("Couldn't load invoices")}
          description={error}
          action={<Button variant="secondary" onClick={load}>{t('Retry')}</Button>}
        />
      ) : loading ? (
        <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div>
      ) : (
        <>
          <DataTable
            columns={columns}
            data={data.items}
            rowKey={(i) => i.id}
            density="comfortable"
            onRowClick={open}
            emptyState={
              <EmptyState
                icon={<Icon.Receipt size={28} />}
                title={hasFilters ? t('No matching invoices') : t('No invoices yet')}
                description={hasFilters ? t('Try adjusting your search or filters.') : t('Raise an invoice against an issued purchase order to start the three-way match.')}
                action={!hasFilters ? <Button variant="secondary" onClick={() => navigate('/invoices/new')}>{t('New invoice')}</Button> : null}
              />
            }
          />
          {data.totalCount > 0 && (
            <div style={{ marginTop: 'var(--space-4)' }}>
              <Pagination page={page} pageCount={totalPages} onPageChange={setPage} totalItems={data.totalCount} pageSize={PAGE_SIZE} />
            </div>
          )}
        </>
      )}
    </PageContainer>
  );
}
